import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ContentItemMeta } from 'app-components/contents/ContentItemMeta';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'ui-components/common/Link';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { IconCountType } from 'ui-components/contents/IconCount';
import { getIconCountMap } from 'utils/content';

interface SearchContentItemProps {
  searchKeyword: string;
  numOfTotalSearchResults: number;
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  listItemIndex?: number;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Search,
};

export const SearchContentItem = ({
  searchKeyword,
  numOfTotalSearchResults,
  content,
  profile,
  listItemIndex,
}: SearchContentItemProps) => {
  const { tracker } = useTrackingState();

  const iconCountMap = useMemo(
    () =>
      getIconCountMap([IconCountType.View, IconCountType.Recommend, IconCountType.Favorite], {
        viewCount: content.counts.viewCount,
        recommendCount: content.counts.recommendCount,
        favoriteCount: content.counts.favoriteCount,
      }),
    [content],
  );
  const handleClick = useCallback(() => {
    tracker.track(TrackingEventType.SelectContentListItem, {
      listType: ContentListType.SearchResult,
      listItemIndex: listItemIndex as number,
      contentId: content.id,
      params: { numOfTotalSearchResults, searchKeyword },
      trackerExtraInfo,
    });
  }, [tracker, listItemIndex, content.id, numOfTotalSearchResults, searchKeyword]);

  return (
    <StyledContainer onClick={handleClick}>
      <Link href={`/contents/${content.id}`}>
        <ContentCover content={content} />
      </Link>
      <StyledMetaContainer>
        <ContentItemMeta
          content={content}
          profile={profile}
          iconCounts={iconCountMap}
          showCategoryType
          showKeyword
          showEpisodeCount
          showDescription
          maxShowKeywordCount={3}
          maxTitleLine={1}
          href={`/contents/${content.id}`}
        />
      </StyledMetaContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const StyledMetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  gap: 4px;
`;
